import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Divider, Form, Popover, Select } from "antd";
import { message } from "utils/ciq-message";
import dayjs from "utils/dayjs-config";
import {
  createOpcSubscriptionConfig,
  getOpcWorkspaces
} from "services/integration-service";
import WarningIcon from "components/svg-icons/warning-icon";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  ErrorMessages,
  IntegrationSystemNameMap,
  SuccessMessages
} from "../../../../constants";
import { AlreadyIntegratedOptionLabel } from "../../common-components";
import { IntegrationsTabSharedContext } from "../integrations-tab-context";

const { Option } = Select;

const getWorkspaceCode = (workspace: any) =>
  workspace?.code ??
  workspace?.workspace_code ??
  workspace?.workspaceCode ??
  "";

export default function OpcInitialIntegration() {
  const { sharedState, dispatch } = useContext(IntegrationsTabSharedContext);
  const [form] = Form.useForm();

  const [workspaces, setWorkspaces] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);

  const onFinish = (values: any) => {
    setSubmittingForm(true);
    const selectedIds: any[] = values.workspace_ids || [];
    const newIntegrationName = sharedState.additionalData.newIntegrationName;
    const isMulti = selectedIds.length > 1;
    const opcAuthOperationId = sharedState.authData.id;

    const payload = selectedIds.map((workspaceId: any) => {
      const workspace = workspaces.find((w: any) => w.id === workspaceId);
      const integrationName = isMulti
        ? `${newIntegrationName} (${workspace.name})`
        : newIntegrationName;

      return {
        opc_auth_operation_id: opcAuthOperationId,
        properties: {
          integration_name: integrationName,
          opc_workspace_id: workspaceId.toString(),
          opc_workspace_name: workspace.name,
          opc_workspace_code: String(getWorkspaceCode(workspace)),
          subscription_alert_email: values.alert_email.join(",")
        }
      };
    });

    const saveData = async () => {
      const createResponse = await createOpcSubscriptionConfig(payload);
      setSubmittingForm(false);

      if (createResponse.success) {
        message.success(SuccessMessages.integration.integrationAdded);
        dispatch({
          type: "SET_ACTIVE_LIST_VIEW"
        });
      } else {
        message.error(ErrorMessages.integration.integrationFailed);
      }
    };
    saveData();
  };

  useEffect(() => {
    let cancelled = false;

    const fetchWorkspaces = async () => {
      try {
        const workspacesResponse = await getOpcWorkspaces(
          sharedState.authData.id
        );
        if (cancelled) return;
        if (workspacesResponse.success) {
          setWorkspaces(workspacesResponse.data ?? []);
        } else {
          setWorkspaces([]);
          message.error("Failed to load OPC workspaces.");
        }
      } catch {
        if (cancelled) return;
        setWorkspaces([]);
        message.error("Failed to load OPC workspaces.");
      }
    };
    fetchWorkspaces();

    return () => {
      cancelled = true;
    };
  }, [sharedState.authData.id]);

  const workspaceListOptions = useMemo(() => {
    const integratedWorkspaceIds = sharedState.integrationConfigs
      .filter((config: any) => config.integration.system === "OPC")
      .map((config: any) => String(config.properties.OPC_WORKSPACE_ID));

    return workspaces
      ?.map((workspace: any) => {
        return {
          label: workspace.name,
          value: workspace.id,
          alreadyIntegrated: integratedWorkspaceIds.includes(
            String(workspace.id)
          )
        };
      })
      .sort((w1: any, w2: any) => {
        if (w1.alreadyIntegrated !== w2.alreadyIntegrated) {
          return w1.alreadyIntegrated ? 1 : -1;
        }
        return w1.label.toLowerCase() > w2.label.toLowerCase() ? 1 : -1;
      });
  }, [workspaces, sharedState.integrationConfigs]);

  const confirmPopoverContent = (
    <div>
      <div className="flex justify-center">
        <WarningIcon />
      </div>
      <div className="flex justify-center text-lg font-medium">
        Please confirm your action
      </div>
      <div className="mt-2 mb-3.5">
        Integration details cannot be edited once saved.
      </div>
      <div className="flex items-center justify-center space-x-2">
        <Button
          onClick={() => {
            setShowConfirmDialog(false);
          }}
          disabled={submittingForm}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={() => {
            form.submit();
          }}
          loading={submittingForm}
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      className="h-full flex flex-col"
      disabled={submittingForm}
    >
      <div className="h-full relative">
        <div className="grow w-full md:max-w-screen-sm mx-auto py-4">
          <div className="mt-5 ">
            <div className="text-sm uppercase font-semibold text-neutral-700">
              You are authorized with{" "}
              {IntegrationSystemNameMap[sharedState.system]}
            </div>
            <div className="text-xs mt-1">
              <span className="text-neutral-600">Last authorized on: </span>
              <span>
                {sharedState.authData?.updated_at
                  ? dayjs(sharedState.authData.updated_at)
                      .local()
                      .format("hh:mmA MM/DD/YYYY")
                  : null}
              </span>
            </div>
          </div>
          <div className="mt-7 w-[500px]">
            <Form.Item
              name="workspace_ids"
              className="hide-required-mark"
              label={
                <div className="flex items-center">
                  <span>Workspace Name</span>
                  <Popover
                    content={
                      <div className="text-sm font-medium max-w-[240px]">
                        Select one or more OPC workspaces to connect with this
                        subscription. Already integrated workspaces are
                        disabled.
                      </div>
                    }
                  >
                    <InfoCircleOutlined className="pl-1 -mt-0.5" />
                  </Popover>
                </div>
              }
              rules={[
                {
                  required: true,
                  message: "Please select at least one Workspace"
                }
              ]}
            >
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                placeholder="Select Workspace"
                className="normal-case"
                loading={!workspaceListOptions}
                allowClear={false}
              >
                {workspaceListOptions?.map((option: any) => {
                  return (
                    <Option
                      key={option.value}
                      value={option.value}
                      label={option.label}
                      disabled={option.alreadyIntegrated}
                    >
                      {option.alreadyIntegrated ? (
                        <AlreadyIntegratedOptionLabel
                          label={option.label}
                          message="Already integrated."
                        />
                      ) : (
                        option.label
                      )}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Form.Item
              name="alert_email"
              className="hide-required-mark"
              label={
                <div className="flex items-center">
                  <span>Notification Recipients</span>
                  <Popover
                    content={
                      <div className="text-sm font-medium">
                        Enter the email address of users who need to be notified
                        in case of failure in authorization.
                      </div>
                    }
                  >
                    <InfoCircleOutlined className="pl-1 -mt-0.5" />
                  </Popover>
                </div>
              }
              rules={[
                { required: true, message: "Please enter at least one email" }
              ]}
            >
              <Select
                className="normal-case"
                mode="tags"
                placeholder="Email IDs"
                notFoundContent={null}
                styles={{ popup: { root: { display: "none" } } }}
              />
            </Form.Item>
          </div>
          <div className="mt-9 grid grid-cols-2 gap-x-4">
            <div className="space-y-1">
              <div className="info-label">
                AUTHORIZATION GRANTED BY (OPC USER):
              </div>
              <div className="info-value">
                {sharedState.authData?.output?.user_info?.given_name}{" "}
                {sharedState.authData?.output?.user_info?.family_name}(
                {sharedState.authData?.output?.user_info?.email})
              </div>
            </div>
            <div className="space-y-1">
              <div className="info-label">AUTHORIZATION INITIATED BY:</div>
              <div className="info-value">
                {sharedState.authData?.created_by_user?.first_name}{" "}
                {sharedState.authData?.created_by_user?.last_name} (
                {sharedState.authData?.created_by_user?.email})
              </div>
            </div>
          </div>
        </div>
      </div>
      <Divider className="mx-0 pb-0 mb-0" />
      <footer className="p-6 flex justify-end gap-x-4">
        <Button
          size="middle"
          onClick={() => {
            dispatch({
              type: "SET_ACTIVE_LIST_VIEW"
            });
          }}
          disabled={submittingForm}
        >
          Back
        </Button>
        <Popover
          placement="topRight"
          content={confirmPopoverContent}
          destroyOnHidden
          open={showConfirmDialog}
        >
          <Button
            size="middle"
            type="primary"
            onClick={async () => {
              try {
                await form.validateFields();
                setShowConfirmDialog(true);
              } catch (ex) {
                console.error(ex);
              }
            }}
            disabled={submittingForm}
          >
            Save
          </Button>
        </Popover>
      </footer>
    </Form>
  );
}
